import { TokenKind, } from "./ast";
import { MISSING_VALUE, isMissing, } from "./type-registry";
import { globalFunctionRegistry, } from "./function-registry";
import { AvgFunction, SumFunction, MinFunction, MaxFunction, CountFunction, StdFunction, SingleFunction, ArrayFirstFunction, ArrayLastFunction, ArrayNthFunction, IfFunction, NotDetectedFunction, NotApplicableFunction, } from "./core-functions";
import { GetFunction, WhereFunction, FindFunction, FindLastFunction, FirstFunction, LastFunction, NthFunction, MaxRowFunction, MinRowFunction, MedianRowFunction, ResampleFunction, } from "./table-functions";
import { timeFunctions } from "./time-functions";
import { mathFunctions } from "./math-functions";
import { textEquals } from "./text-equals";
import { convertUnit, getDimensionForUnit, getDisplaySymbol, SCALAR_UNIT, TIMESTAMP_UNIT_SECONDS, } from "../unit-system";
import { parseFormula } from "./parser";
// ---------------------------------------------------------------------------
// Module-level function registration (runs once)
// ---------------------------------------------------------------------------
const coreFunctions = [
    new AvgFunction(),
    new SumFunction(),
    new MinFunction(),
    new MaxFunction(),
    new CountFunction(),
    new StdFunction(),
    new SingleFunction(),
    // Array-based first/last/nth must register BEFORE the table-based ones so
    // the overload resolver finds the array variant when given an array.
    new ArrayFirstFunction(),
    new ArrayLastFunction(),
    new ArrayNthFunction(),
    new IfFunction(),
    new NotDetectedFunction(),
    new NotApplicableFunction(),
    new GetFunction(),
    new WhereFunction(),
    new FindFunction(),
    new FindLastFunction(),
    new FirstFunction(),
    new LastFunction(),
    new NthFunction(),
    new MaxRowFunction(),
    new MinRowFunction(),
    new MedianRowFunction(),
    new ResampleFunction(),
    ...timeFunctions,
    ...mathFunctions,
];
const registeredSignatures = new Set(globalFunctionRegistry
    .getAvailable()
    .map((s) => `${s.name}(${s.parameters.map((p) => p.type).join(",")})`));
for (const fn of coreFunctions) {
    const sig = `${fn.signature.name}(${fn.signature.parameters.map((p) => p.type).join(",")})`;
    if (!registeredSignatures.has(sig)) {
        globalFunctionRegistry.register(fn);
        registeredSignatures.add(sig);
    }
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function createError(type, message) {
    return { type, message };
}
function isError(value) {
    return (typeof value === "object" &&
        value !== null &&
        "type" in value &&
        "message" in value);
}
// ---------------------------------------------------------------------------
// FormulaEvaluator — evaluates a single AST against a flat variable map
// ---------------------------------------------------------------------------
export class FormulaEvaluator {
    variables;
    computedValues;
    functionRegistry;
    /**
     * Where non-fatal findings go, shared with row-scope evaluators so none are
     * lost. Only a caller that passes one can show a warning, so only then are
     * mixed units of one kind added as a warning rather than refused.
     */
    warnings;
    constructor(variables, computedValues, warnings) {
        this.variables = variables;
        this.computedValues = computedValues ?? new Map();
        this.functionRegistry = globalFunctionRegistry;
        this.warnings = warnings;
    }
    createError(type, message) {
        return createError(type, message);
    }
    evaluate(node) {
        switch (node.type) {
            case "Literal":
                return this.evaluateLiteral(node);
            case "VariableAccess":
                return this.evaluateVariableAccess(node);
            case "FunctionCall":
                return this.evaluateFunctionCall(node);
            case "MemberAccess":
                return this.evaluateMemberAccess(node);
            case "BinaryOperation":
                return this.evaluateBinaryOperation(node);
            case "UnaryOperation":
                return this.evaluateUnaryOperation(node);
            case "Comparison":
                return this.evaluateComparison(node);
            case "Logical":
                return this.evaluateLogical(node);
            case "Not":
                return this.evaluateNot(node);
        }
    }
    /**
     * Evaluate a deferred AST (a `predicate`/`column_ref` argument) against a
     * single row. The row's cells are overlaid on the outer variables keyed by
     * column code, so a bare identifier in the predicate resolves to the column
     * first (column-scope), while `upstream.*`/`<abbr>.*` still resolve via the
     * outer scope. Used by the criterion table functions (find/where/maxrow/…).
     */
    evaluateInRowScope(ast, rowVars) {
        const merged = new Map(this.variables);
        for (const [k, v] of rowVars)
            merged.set(k, v);
        return new FormulaEvaluator(merged, this.computedValues, this.warnings).evaluate(ast);
    }
    evaluateMemberAccess(node) {
        const object = this.evaluate(node.object);
        if (isError(object))
            return object;
        // resolvePropertyChain handles object→field and table→column-array, plus
        // missing propagation and the not-an-object error.
        return this.resolvePropertyChain(object, "(expression)", [node.property]);
    }
    evaluateComparison(node) {
        const left = this.evaluate(node.left);
        if (isError(left))
            return left;
        const right = this.evaluate(node.right);
        if (isError(right))
            return right;
        if (isMissing(left) || isMissing(right))
            return MISSING_VALUE;
        // Text compares only with text, and only for (in)equality: "TC" == organism.
        if (left.type === "text" || right.type === "text") {
            if (left.type !== "text" || right.type !== "text") {
                return createError("InvalidArgumentType", `Comparison "${node.operator}" cannot compare text with a number.`);
            }
            if (node.operator !== "==" && node.operator !== "!=") {
                return createError("UnsupportedOperator", `Text can only be compared with == or !=, not "${node.operator}".`);
            }
            const equal = textEquals(left.value, right.value);
            return { type: "boolean", value: node.operator === "==" ? equal : !equal };
        }
        if (left.type !== "scalar" ||
            right.type !== "scalar" ||
            typeof left.value !== "number" ||
            typeof right.value !== "number") {
            return createError("InvalidArgumentType", `Comparison "${node.operator}" expected numeric operands, got "${left.type}" and "${right.type}".`);
        }
        const l = left.value;
        const r = right.value;
        let result;
        switch (node.operator) {
            case "==":
                result = l === r;
                break;
            case "!=":
                result = l !== r;
                break;
            case ">":
                result = l > r;
                break;
            case "<":
                result = l < r;
                break;
            case ">=":
                result = l >= r;
                break;
            case "<=":
                result = l <= r;
                break;
        }
        return { type: "boolean", value: result };
    }
    evaluateLogical(node) {
        const left = this.evaluate(node.left);
        if (isError(left))
            return left;
        if (isMissing(left))
            return MISSING_VALUE;
        if (left.type !== "boolean") {
            return createError("InvalidArgumentType", `Operator "${node.operator}" expected boolean operands, got "${left.type}".`);
        }
        // Short-circuit.
        if (node.operator === "or" && left.value === true)
            return { type: "boolean", value: true };
        if (node.operator === "and" && left.value === false)
            return { type: "boolean", value: false };
        const right = this.evaluate(node.right);
        if (isError(right))
            return right;
        if (isMissing(right))
            return MISSING_VALUE;
        if (right.type !== "boolean") {
            return createError("InvalidArgumentType", `Operator "${node.operator}" expected boolean operands, got "${right.type}".`);
        }
        return { type: "boolean", value: Boolean(right.value) };
    }
    evaluateNot(node) {
        const operand = this.evaluate(node.operand);
        if (isError(operand))
            return operand;
        if (isMissing(operand))
            return MISSING_VALUE;
        if (operand.type !== "boolean") {
            return createError("InvalidArgumentType", `Operator "not" expected a boolean operand, got "${operand.type}".`);
        }
        return { type: "boolean", value: !operand.value };
    }
    evaluateLiteral(node) {
        if (typeof node.value === "string") {
            return { type: "text", value: node.value };
        }
        return { type: "scalar", value: node.value, unit: SCALAR_UNIT };
    }
    evaluateVariableAccess(node) {
        const value = this.variables.get(node.identifier) ??
            this.computedValues.get(node.identifier);
        if (!value) {
            return createError("UndefinedVariable", `Variable "${node.identifier}" not found.`);
        }
        if (isError(value)) {
            return value;
        }
        return this.resolvePropertyChain(value, node.identifier, node.properties);
    }
    resolvePropertyChain(value, rootName, properties) {
        let current = value;
        let path = rootName;
        for (const prop of properties) {
            path = `${path}.${prop}`;
            // A missing value has no properties to access — stay missing.
            if (isMissing(current))
                return current;
            if (current.type === "table") {
                return this.evaluateTableColumnAccess(current, prop, path);
            }
            if (current.type === "object") {
                const objValue = current.value;
                if (!Object.prototype.hasOwnProperty.call(objValue, prop)) {
                    return createError("UndefinedProperty", `Property "${prop}" not found on "${path}".`);
                }
                current = objValue[prop];
                continue;
            }
            return createError("PropertyAccessOnNonObject", `Cannot access property "${prop}" on type "${current.type}" at "${path}".`);
        }
        return current;
    }
    evaluateTableColumnAccess(tableValue, columnName, path) {
        const table = tableValue.value;
        // Special _index column
        if (columnName === "_index") {
            if (!table.indexColumn) {
                return createError("UndefinedColumn", `Table "${path}" does not have an index column.`);
            }
            const indexValues = table.rows
                .map((row) => row.index)
                .filter((val) => val !== null && val !== undefined)
                .map((val) => {
                let unit = SCALAR_UNIT;
                let numericValue;
                if (table.schema.indexColumn?.type === "timestamp") {
                    numericValue = new Date(val).getTime() / 1000;
                    unit = TIMESTAMP_UNIT_SECONDS;
                }
                else if (typeof val === "number") {
                    numericValue = val;
                }
                else {
                    numericValue = 0;
                }
                return {
                    value: numericValue,
                    unit,
                    timestamp: table.schema.indexColumn?.type === "timestamp"
                        ? new Date(val)
                        : undefined,
                };
            });
            return {
                type: "array",
                value: indexValues,
                metadata: {
                    sourceTable: path,
                    sourceColumn: "_index",
                    columnType: table.schema.indexColumn?.type,
                },
            };
        }
        // Regular column
        const column = table.schema.columns.find((col) => col.name === columnName);
        if (!column) {
            return createError("UndefinedColumn", `Column "${columnName}" not found in table "${path}".`);
        }
        const columnValues = table.rows.map((row) => {
            const cellValue = row.data[columnName];
            if (cellValue === null || cellValue === undefined) {
                return { value: NaN, unit: column.unit ?? SCALAR_UNIT };
            }
            if (column.type === "datetime") {
                return {
                    value: new Date(cellValue).getTime() / 1000,
                    unit: TIMESTAMP_UNIT_SECONDS,
                    timestamp: new Date(cellValue),
                };
            }
            return {
                value: typeof cellValue === "number"
                    ? cellValue
                    : parseFloat(String(cellValue)),
                unit: column.unit ?? SCALAR_UNIT,
            };
        });
        return {
            type: "array",
            value: columnValues,
            metadata: {
                sourceTable: path,
                sourceColumn: columnName,
                columnType: column.type,
            },
        };
    }
    evaluateFunctionCall(node) {
        // Positions declared `lazy` (predicate / column_ref slots) are NOT
        // pre-evaluated — they're passed as a `deferred` value wrapping the raw AST,
        // and the function evaluates them per row via evaluateInRowScope.
        const lazyPositions = this.functionRegistry.lazyParamPositions(node.functionName);
        const args = [];
        for (let i = 0; i < node.arguments.length; i++) {
            const argNode = node.arguments[i];
            if (lazyPositions.has(i)) {
                args.push({ type: "deferred", value: argNode });
                continue;
            }
            const argResult = this.evaluate(argNode);
            if (isError(argResult))
                return argResult;
            args.push(argResult);
        }
        const func = this.functionRegistry.resolve(node.functionName, args);
        if (!func) {
            return createError("FunctionNotFound", `Function "${node.functionName}" not found or no matching signature.`);
        }
        // Non-aggregate functions propagate: any `missing` argument short-circuits
        // the whole call to `missing`. Aggregates (handlesMissing) instead receive
        // the args and skip the missing ones themselves.
        if (!func.handlesMissing && args.some(isMissing)) {
            return MISSING_VALUE;
        }
        const validation = this.functionRegistry.validate(node.functionName, args);
        if (validation)
            return validation;
        return func.execute(args, this);
    }
    evaluateBinaryOperation(node) {
        const left = this.evaluate(node.left);
        if (isError(left))
            return left;
        const right = this.evaluate(node.right);
        if (isError(right))
            return right;
        // Propagate emptiness: an operation involving a missing operand is missing.
        if (isMissing(left) || isMissing(right))
            return MISSING_VALUE;
        if (left.type !== "scalar" || right.type !== "scalar") {
            return createError("InvalidArgumentType", `Binary operator "${TokenKind[node.operator]}" expected scalar values, got "${left.type}" and "${right.type}".`);
        }
        const opKind = node.operator;
        // ---- Timestamp arithmetic (the magic epoch-seconds unit) -------------
        // A timestamp is an instant on the timeline; a duration is a Time-dimension
        // scalar (s/min/h). The allowed algebra is affine:
        //   timestamp − timestamp → duration (seconds)
        //   timestamp ± duration  → timestamp
        //   duration  + timestamp → timestamp
        // Everything else (timestamp + timestamp, timestamp × scalar, …) is an error.
        const leftIsTs = left.unit === TIMESTAMP_UNIT_SECONDS;
        const rightIsTs = right.unit === TIMESTAMP_UNIT_SECONDS;
        if (leftIsTs || rightIsTs) {
            if (opKind !== TokenKind.Add && opKind !== TokenKind.Sub) {
                return createError("InvalidOperation", `Operator "${TokenKind[opKind]}" is not supported for timestamp operands.`);
            }
            // timestamp ± timestamp
            if (leftIsTs && rightIsTs) {
                if (opKind === TokenKind.Sub) {
                    return { type: "scalar", value: left.value - right.value, unit: "s" };
                }
                return createError("InvalidOperation", "Cannot add two timestamps; subtract them to get a duration instead.");
            }
            // timestamp ± duration  (or  duration + timestamp)
            const other = leftIsTs ? right : left;
            const otherUnit = other.unit ?? SCALAR_UNIT;
            if (getDimensionForUnit(otherUnit) !== "Time") {
                return createError("InvalidOperation", `A timestamp can only be combined with a time duration (s/min/h), not unit "${otherUnit}".`);
            }
            // duration − timestamp is meaningless
            if (opKind === TokenKind.Sub && !leftIsTs) {
                return createError("InvalidOperation", "Cannot subtract a timestamp from a duration.");
            }
            const durationSeconds = convertUnit(other.value, otherUnit, "s");
            const tsValue = leftIsTs ? left.value : right.value;
            const value = opKind === TokenKind.Add
                ? tsValue + durationSeconds
                : tsValue - durationSeconds;
            return { type: "scalar", value, unit: TIMESTAMP_UNIT_SECONDS };
        }
        if (opKind === TokenKind.Add || opKind === TokenKind.Sub) {
            const leftUnit = left.unit ?? SCALAR_UNIT;
            const rightUnit = right.unit ?? SCALAR_UNIT;
            let resultUnit = leftUnit;
            if (leftUnit !== rightUnit) {
                if (leftUnit === SCALAR_UNIT) {
                    resultUnit = rightUnit;
                }
                else if (rightUnit === SCALAR_UNIT) {
                    resultUnit = leftUnit;
                }
                else if (this.warnings !== undefined &&
                    getDimensionForUnit(leftUnit) !== undefined &&
                    getDimensionForUnit(leftUnit) === getDimensionForUnit(rightUnit)) {
                    // Same kind of quantity in different units (mL and µL). Dividing by
                    // a plain number keeps a unit, so a conversion written into the
                    // formula cannot be told apart from a forgotten one: add the numbers
                    // as entered and leave the check to the author.
                    const warning = `${opKind === TokenKind.Add ? "Added" : "Subtracted"} values in different units ("${getDisplaySymbol(leftUnit)}" and "${getDisplaySymbol(rightUnit)}") as entered, without converting. Check that the formula converts one to the other.`;
                    if (!this.warnings.includes(warning))
                        this.warnings.push(warning);
                }
                else {
                    return createError("InvalidOperation", `Cannot ${opKind === TokenKind.Add ? "add" : "subtract"} values with different units: "${leftUnit}" and "${rightUnit}".`);
                }
            }
            const value = opKind === TokenKind.Add
                ? left.value + right.value
                : left.value - right.value;
            return { type: "scalar", value, unit: resultUnit };
        }
        if (opKind === TokenKind.Mul || opKind === TokenKind.Div) {
            // Timestamp operands are already handled (and rejected) above.
            if (opKind === TokenKind.Div && right.value === 0) {
                return createError("DivisionByZero", "Division by zero encountered during evaluation.");
            }
            const value = opKind === TokenKind.Mul
                ? left.value * right.value
                : left.value / right.value;
            const leftUnit = left.unit ?? SCALAR_UNIT;
            const rightUnit = right.unit ?? SCALAR_UNIT;
            let resultUnit = SCALAR_UNIT;
            if (opKind === TokenKind.Mul) {
                // scalar * X = X, X * scalar = X, X * Y = scalar (composite not modeled)
                if (leftUnit === SCALAR_UNIT)
                    resultUnit = rightUnit;
                else if (rightUnit === SCALAR_UNIT)
                    resultUnit = leftUnit;
            }
            else {
                // X / X = scalar (dimensionless ratio), X / scalar = X, otherwise scalar
                if (leftUnit === rightUnit)
                    resultUnit = SCALAR_UNIT;
                else if (rightUnit === SCALAR_UNIT)
                    resultUnit = leftUnit;
            }
            return { type: "scalar", value, unit: resultUnit };
        }
        return createError("UnsupportedOperator", `Binary operator "${TokenKind[node.operator]}" is not supported.`);
    }
    evaluateUnaryOperation(node) {
        const operand = this.evaluate(node.operand);
        if (isError(operand))
            return operand;
        if (isMissing(operand))
            return MISSING_VALUE;
        if (operand.type !== "scalar" || typeof operand.value !== "number") {
            return createError("InvalidArgumentType", `Unary operator "${TokenKind[node.operator]}" expected a scalar value, got "${operand.type}".`);
        }
        switch (node.operator) {
            case TokenKind.Sub:
                return { type: "scalar", value: -operand.value, unit: operand.unit };
            case TokenKind.Add:
                return { type: "scalar", value: +operand.value, unit: operand.unit };
            default:
                return createError("UnsupportedOperator", `Unary operator "${TokenKind[node.operator]}" is not supported.`);
        }
    }
}
// ---------------------------------------------------------------------------
// Float-noise stripping
// ---------------------------------------------------------------------------
/**
 * Round a final result to 12 significant figures to strip IEEE-754
 * representation noise (e.g. 19.999999999999602 → 20) without harming genuine
 * precision — doubles carry ~15-16 sig figs and noise appears at the 15th-16th,
 * while 12 sig figs far exceeds any realistic measurement precision. Applied
 * ONLY at result emission, never to intermediate arithmetic.
 */
export function stripFloatNoise(value) {
    return Number.isFinite(value) ? Number(value.toPrecision(12)) : value;
}
// ---------------------------------------------------------------------------
// evaluateFormulas — batch evaluation with dependency resolution
// ---------------------------------------------------------------------------
export function evaluateFormulas(formulas, variables) {
    // Parse all formulas
    const parsed = new Map();
    const results = new Map();
    for (const def of formulas) {
        try {
            parsed.set(def.name, { def, parsed: parseFormula(def.formula) });
        }
        catch (e) {
            results.set(def.name, {
                success: false,
                error: createError("InvalidOperation", `Failed to parse formula "${def.formula}": ${e instanceof Error ? e.message : String(e)}`),
            });
        }
    }
    // Topological evaluation with cycle detection
    const computedValues = new Map();
    const inProgress = new Set();
    const formulaNames = new Set(parsed.keys());
    const evaluateOne = (name) => {
        if (computedValues.has(name) || results.has(name))
            return;
        if (inProgress.has(name)) {
            const error = createError("InvalidOperation", `Cyclic dependency detected involving formula "${name}".`);
            computedValues.set(name, error);
            results.set(name, { success: false, error });
            return;
        }
        const entry = parsed.get(name);
        if (!entry)
            return;
        inProgress.add(name);
        // Evaluate dependencies that are other formulas first. A failed one fails
        // this formula only where it is read, so an if() branch not taken does not,
        // whatever order the formulas come in.
        for (const dep of entry.parsed.dependencies) {
            if (formulaNames.has(dep) && !computedValues.has(dep) && !results.has(dep)) {
                evaluateOne(dep);
            }
        }
        // Evaluate this formula
        const warnings = [];
        const evaluator = new FormulaEvaluator(variables, computedValues, warnings);
        const evalResult = evaluator.evaluate(entry.parsed.ast);
        if (isError(evalResult)) {
            computedValues.set(name, evalResult);
            results.set(name, { success: false, error: evalResult });
        }
        else if (evalResult.type === "scalar") {
            computedValues.set(name, evalResult);
            // Apply unit conversion
            const sourceUnit = entry.def.resultBaseUnit ?? evalResult.unit ?? SCALAR_UNIT;
            const displayUnit = entry.def.resultDisplayUnit ?? sourceUnit;
            const convertedValue = convertUnit(evalResult.value, sourceUnit, displayUnit);
            results.set(name, {
                success: true,
                value: stripFloatNoise(convertedValue),
                unit: displayUnit,
                ...(warnings.length > 0 ? { warnings } : {}),
            });
        }
        else if (isMissing(evalResult)) {
            const error = createError("InvalidOperation", `Formula "${name}" evaluated to an empty value because one or more referenced inputs were blank.`);
            computedValues.set(name, error);
            results.set(name, { success: false, error });
        }
        else if (evalResult.type === "array") {
            const error = createError("ArrayNotAllowedAsResult", `Formula "${name}" evaluated to an array. Reduce it with an aggregation function like avg(), sum(), single(), count(), std(), first(), last(), min(), or max().`);
            computedValues.set(name, error);
            results.set(name, { success: false, error });
        }
        else {
            const error = createError("UnsupportedResultType", `Formula "${name}" evaluated to non-scalar type "${evalResult.type}".`);
            computedValues.set(name, error);
            results.set(name, { success: false, error });
        }
        inProgress.delete(name);
    };
    for (const name of parsed.keys()) {
        evaluateOne(name);
    }
    return results;
}
// ---------------------------------------------------------------------------
// Exports for UI: available functions list
// ---------------------------------------------------------------------------
export function getAvailableFunctions() {
    return globalFunctionRegistry.getAvailable().map((sig) => ({
        name: sig.name,
        description: sig.description,
        args: sig.parameters.map((p) => p.name),
    }));
}
//# sourceMappingURL=evaluator.js.map